/**
 * Helpers for displaying CNPJ-enriched company data (BrasilAPI / OpenCNPJ / CNPJá).
 * These providers are inconsistent: capital_social may come as a number (10000)
 * or as a Brazilian-formatted string ("10.000,00"), and "tipo" must be derived
 * from natureza_juridica + porte + opcao_pelo_mei (different legal dimensions).
 */

// Strip accents and lowercase so we can match natureza/porte regardless of casing.
const normalize = str =>
  String(str || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();

/**
 * Parse capital_social into a Number, handling both raw numbers and
 * Brazilian-formatted strings. Returns null when there is no usable value
 * (prevents the "R$ NaN" bug).
 */
export const parseCapitalSocial = raw => {
  if (raw === null || raw === undefined || raw === '') return null;
  if (typeof raw === 'number') return Number.isNaN(raw) ? null : raw;

  let cleaned = String(raw).replace(/[^\d.,-]/g, '');
  if (cleaned === '') return null;

  // Brazilian format "10.000,00" -> drop thousands dots, comma becomes decimal.
  if (cleaned.includes(',')) {
    cleaned = cleaned.replace(/\./g, '').replace(',', '.');
  }

  const num = Number(cleaned);
  return Number.isNaN(num) ? null : num;
};

/** Format capital_social as BRL currency, or null when there's no value. */
export const formatCapitalSocial = raw => {
  const num = parseCapitalSocial(raw);
  if (num === null) return null;
  return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

// Map the official natureza_juridica text to a short, sales-friendly sigla.
const naturezaSigla = naturezaJuridica => {
  const n = normalize(naturezaJuridica);
  if (!n) return null;
  if (n.includes('responsabilidade limitada') || n.includes('eireli'))
    return 'EIRELI';
  if (n.includes('limitada')) return 'LTDA';
  if (n.includes('anonima')) return 'S/A';
  if (n.includes('empresario')) return 'EI';
  if (n.includes('cooperativa')) return 'Cooperativa';
  if (n.includes('sociedade simples')) return 'SS';
  return null;
};

// Map porte to a short sigla. "Demais" carries no useful signal.
const porteSigla = porte => {
  const p = normalize(porte);
  if (!p) return null;
  if (p.includes('micro empresa') || p === 'me' || p.includes('microempresa'))
    return 'ME';
  if (p.includes('pequeno porte') || p === 'epp') return 'EPP';
  return null;
};

/**
 * Derive a short company type label (e.g. "MEI", "LTDA · EPP", "S/A").
 * MEI takes precedence since it is the most relevant tag for sales context.
 * Returns null when nothing meaningful can be derived (caller falls back to
 * showing the full natureza_juridica text).
 */
export const companyTipo = ({
  natureza_juridica: naturezaJuridica,
  porte,
  opcao_pelo_mei: opcaoPeloMei,
} = {}) => {
  if (opcaoPeloMei === true || normalize(opcaoPeloMei) === 'sim') return 'MEI';

  const parts = [];
  const natureza = naturezaSigla(naturezaJuridica);
  if (natureza) parts.push(natureza);
  const porteTag = porteSigla(porte);
  if (porteTag) parts.push(porteTag);

  return parts.length ? parts.join(' · ') : null;
};
